import { createSelector } from 'redux-orm';

import orm from '../orm';
import { selectPath } from './router';
import { selectCurrentModal, selectCurrentModalData } from './modals';
import ModalTypes from '../constants/ModalTypes';

const pickBoardLevel = (pointSystems, boardId) =>
  pointSystems.find((ps) => ps.boardId != null && String(ps.boardId) === String(boardId)) || null;

/**
 * Для доски — board-level PS; isEnabled — с Board.isPointSystemEnabled.
 */
const resolvePointSystemForBoard = (session, boardId) => {
  const { Board, PointSystem } = session;
  const boardModel = Board.withId(boardId);
  if (!boardModel) {
    return null;
  }

  const boardLevel = pickBoardLevel(PointSystem.all().toRefArray(), boardId);
  if (!boardLevel) {
    return null;
  }

  return {
    ...boardLevel,
    isEnabled: boardModel.isPointSystemEnabled === true,
  };
};

export const selectPointSystemForCurrentBoard = createSelector(
  orm,
  (state) => selectPath(state).boardId,
  (session, boardId) => {
    if (boardId) {
      return resolvePointSystemForBoard(session, boardId);
    }

    return null;
  },
);

/** @deprecated */
export const selectProjectLevelPointSystemForCurrentProject = () => null;

export const selectBoardLevelPointSystemForCurrentBoard = createSelector(
  orm,
  (state) => selectPath(state).boardId,
  ({ PointSystem }, boardId) => {
    if (!boardId) {
      return null;
    }

    return pickBoardLevel(PointSystem.all().toRefArray(), boardId);
  },
);

/**
 * Настройки модалки:
 * - mode=templates + selectedTemplateId → global template PS
 * - иначе board-level текущей доски
 */
export const selectPointSystemForSettingsModal = (state) => {
  const currentModal = selectCurrentModal(state);
  const modalData = selectCurrentModalData(state);

  if (currentModal === ModalTypes.POINT_SYSTEM && modalData?.selectedTemplateId) {
    const session = orm.session(state.orm);
    const model = session.PointSystem.withId(modalData.selectedTemplateId);
    if (model) {
      return model.ref;
    }
  }

  return selectPointSystemForCurrentBoard(state);
};

export const makeSelectPointSystemByBoardId = () =>
  createSelector(orm, (_, boardId) => boardId, (session, boardId) => {
    if (!boardId) {
      return null;
    }

    return resolvePointSystemForBoard(session, boardId);
  });

/**
 * Доски (с названием проекта), к которым применён global-шаблон PointSystem.
 * Возвращает { active: [], archived: [] }.
 */
export const selectBoardsUsingPointSystemTemplate = createSelector(
  orm,
  (_, templateId) => templateId,
  ({ PointSystem, Board, Project }, templateId) => {
    const empty = { active: [], archived: [] };

    if (!templateId) {
      return empty;
    }

    const tid = String(templateId);

    const items = PointSystem.all()
      .toRefArray()
      .filter(
        (ps) =>
          ps.boardId != null &&
          ps.boardId !== '' &&
          ps.sourcePointSystemId != null &&
          String(ps.sourcePointSystemId) === tid,
      )
      .map((ps) => {
        const boardModel = Board.withId(ps.boardId);
        if (!boardModel) {
          return null;
        }

        const boardRef = boardModel.ref;
        const boardName = boardRef.name || '';
        const projectId = boardRef.projectId;
        const projectModel = projectId != null ? Project.withId(projectId) : null;
        const projectName = projectModel ? projectModel.ref.name || '' : '';
        const label = projectName ? `${projectName} / ${boardName}` : boardName;

        if (!label) {
          return null;
        }

        return {
          boardId: String(ps.boardId),
          boardName,
          projectName,
          label,
          isArchived: boardRef.is_archive === true,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label, 'ru'));

    return {
      active: items.filter((item) => !item.isArchived),
      archived: items.filter((item) => item.isArchived),
    };
  },
);

export default {
  selectPointSystemForCurrentBoard,
  selectProjectLevelPointSystemForCurrentProject,
  selectBoardLevelPointSystemForCurrentBoard,
  selectPointSystemForSettingsModal,
  makeSelectPointSystemByBoardId,
  selectBoardsUsingPointSystemTemplate,
};
